// Achievement definitions and tracking system
export const ACHIEVEMENTS = {
  // Founder's Club - First 100 teams
  FOUNDERS_CLUB: {
    id: 'founders_club',
    name: "Founder's Club",
    description: 'One of the first 100 teams to join Trivia Plug',
    icon: '👑',
    category: 'exclusive',
    requirement: 'Be among the first 100 teams to register'
  },

  // Founder Badge - Special for you
  FOUNDER: {
    id: 'founder',
    name: 'Founder',
    description: 'The creator of Trivia Plug',
    icon: '🌟',
    category: 'exclusive',
    requirement: 'Platform founder'
  },

  // Company Founder Badge - Most prestigious
  COMPANY_FOUNDER: {
    id: 'company_founder',
    name: 'Company Founder',
    description: 'The founder and CEO of Trivia Plug',
    icon: '👑',
    category: 'exclusive',
    requirement: 'Company founder'
  },

  // GPA Milestones
  SCHOLAR: {
    id: 'scholar',
    name: 'Scholar',
    description: 'Achieve a 3.0 GPA (5.0 scale)',
    icon: '🏆',
    category: 'performance',
    requirement: 'GPA >= 3.0'
  },
  HONOR_STUDENT: {
    id: 'honor_student',
    name: 'Honor Student',
    description: 'Achieve a 3.5 GPA (5.0 scale)',
    icon: '🎓',
    category: 'performance',
    requirement: 'GPA >= 3.5'
  },
  GENIUS: {
    id: 'genius',
    name: 'Genius',
    description: 'Achieve a 4.0 GPA (5.0 scale)',
    icon: '🧠',
    category: 'performance',
    requirement: 'GPA >= 4.0'
  },
  PERFECT_SCORE: {
    id: 'perfect_score',
    name: 'Perfect Score',
    description: 'Achieve a 4.0 GPA with 10+ games',
    icon: '⭐',
    category: 'performance',
    requirement: 'GPA >= 4.0 (5.0 scale) AND games >= 10'
  },

  // Game Count Milestones
  ROOKIE: {
    id: 'rookie',
    name: 'Rookie',
    description: 'Play 5 games',
    icon: '🎮',
    category: 'milestone',
    requirement: 'Games played >= 5'
  },
  REGULAR: {
    id: 'regular',
    name: 'Regular',
    description: 'Play 25 games',
    icon: '🎯',
    category: 'milestone',
    requirement: 'Games played >= 25'
  },
  VETERAN: {
    id: 'veteran',
    name: 'Veteran',
    description: 'Play 50 games',
    icon: '🎖️',
    category: 'milestone',
    requirement: 'Games played >= 50'
  },
  LEGEND: {
    id: 'legend',
    name: 'Legend',
    description: 'Play 100 games',
    icon: '🏅',
    category: 'milestone',
    requirement: 'Games played >= 100'
  },

  // Points Milestones
  POINT_COLLECTOR: {
    id: 'point_collector',
    name: 'Point Collector',
    description: 'Earn 1,000 total points',
    icon: '💰',
    category: 'milestone',
    requirement: 'Total points >= 1000'
  },
  POINT_MASTER: {
    id: 'point_master',
    name: 'Point Master',
    description: 'Earn 5,000 total points',
    icon: '💎',
    category: 'milestone',
    requirement: 'Total points >= 5000'
  },
  POINT_LEGEND: {
    id: 'point_legend',
    name: 'Point Legend',
    description: 'Earn 10,000 total points',
    icon: '👑',
    category: 'milestone',
    requirement: 'Total points >= 10000'
  },

  // Team Size Achievements
  SQUAD: {
    id: 'squad',
    name: 'Squad',
    description: 'Have 4 team members',
    icon: '👥',
    category: 'team',
    requirement: 'Team members >= 4'
  },
  ENSEMBLE: {
    id: 'ensemble',
    name: 'Ensemble',
    description: 'Have 6 team members (full team)',
    icon: '🎭',
    category: 'team',
    requirement: 'Team members >= 6'
  },
  TEAM_PLAYER: {
    id: 'team_player',
    name: 'Team Player',
    description: 'Add 3 new members to your team',
    icon: '🤝',
    category: 'team',
    requirement: 'Added 3+ members'
  },

  // Social Achievements
  GOOD_SPORT: {
    id: 'good_sport',
    name: 'Good Sport',
    description: 'Rate 10 different hosts/games',
    icon: '🤝',
    category: 'social',
    requirement: 'Rate 10+ hosts/games'
  },
  FEEDBACK_MASTER: {
    id: 'feedback_master',
    name: 'Feedback Master',
    description: 'Leave comments on 20 ratings',
    icon: '💬',
    category: 'social',
    requirement: 'Leave 20+ rating comments'
  },
  TEAM_CAPTAIN: {
    id: 'team_captain',
    name: 'Team Captain',
    description: 'Be a team captain for 30 days',
    icon: '⚓',
    category: 'social',
    requirement: 'Captain for 30+ days'
  }
};

// Check if team qualifies for Founder's Club (first 100 teams)
export const checkFoundersClub = async (teamId, db) => {
  try {
    const { collection, query, orderBy, limit, getDocs } = await import('firebase/firestore');
    
    // Get all teams ordered by creation date
    const teamsQuery = query(
      collection(db, 'teams'),
      orderBy('createdAt', 'asc'),
      limit(100)
    );
    const teamsSnapshot = await getDocs(teamsQuery);
    
    // Check if this team is in the first 100
    const isFounder = teamsSnapshot.docs.some(doc => doc.id === teamId);
    return isFounder;
  } catch (error) {
    console.error('Error checking Founder\'s Club:', error);
    return false;
  }
};

// Check if user is the founder (you can set your user ID here)
export const checkFounder = (userId) => {
  // Replace 'your-actual-user-id-here' with your Firebase User UID
  // To find your UID: 
  // 1. Open browser console in your app
  // 2. Type: console.log('Your User ID:', firebase.auth().currentUser?.uid)
  // 3. Copy the ID and replace it below
  const FOUNDER_USER_ID = '25ASAtsXhQYFIR3wOX2m2TTaJxD3';
  return userId === FOUNDER_USER_ID;
};

// Check if user is the company founder (CEO) - Team side
export const checkCompanyFounder = (userId, userEmail) => {
  // Check for the team-side CEO email
  const TEAM_CEO_EMAIL = 'dmcallday1234@yahoo.com';
  return userEmail === TEAM_CEO_EMAIL;
};

// Check and award achievements based on team data
export const checkAchievements = (teamData) => {
  const newAchievements = [];
  const currentAchievements = teamData.achievements || [];

  // GPA-based achievements
  if (teamData.gpa >= 3.0 && !currentAchievements.includes('scholar')) {
    newAchievements.push('scholar');
  }
  if (teamData.gpa >= 3.5 && !currentAchievements.includes('honor_student')) {
    newAchievements.push('honor_student');
  }
  if (teamData.gpa >= 4.0 && !currentAchievements.includes('genius')) {
    newAchievements.push('genius');
  }
  if (teamData.gpa >= 4.0 && teamData.gamesPlayed >= 10 && !currentAchievements.includes('perfect_score')) {
    newAchievements.push('perfect_score');
  }

  // Game count achievements
  if (teamData.gamesPlayed >= 5 && !currentAchievements.includes('rookie')) {
    newAchievements.push('rookie');
  }
  if (teamData.gamesPlayed >= 25 && !currentAchievements.includes('regular')) {
    newAchievements.push('regular');
  }
  if (teamData.gamesPlayed >= 50 && !currentAchievements.includes('veteran')) {
    newAchievements.push('veteran');
  }
  if (teamData.gamesPlayed >= 100 && !currentAchievements.includes('legend')) {
    newAchievements.push('legend');
  }

  // Points achievements
  if (teamData.totalPoints >= 1000 && !currentAchievements.includes('point_collector')) {
    newAchievements.push('point_collector');
  }
  if (teamData.totalPoints >= 5000 && !currentAchievements.includes('point_master')) {
    newAchievements.push('point_master');
  }
  if (teamData.totalPoints >= 10000 && !currentAchievements.includes('point_legend')) {
    newAchievements.push('point_legend');
  }

  // Team size achievements
  if (teamData.members && teamData.members.length >= 4 && !currentAchievements.includes('squad')) {
    newAchievements.push('squad');
  }
  if (teamData.members && teamData.members.length >= 6 && !currentAchievements.includes('ensemble')) {
    newAchievements.push('ensemble');
  }

  return newAchievements;
};

// Get achievement display info
export const getAchievementInfo = (achievementId) => {
  return ACHIEVEMENTS[achievementId.toUpperCase()] || null;
};

// Get all achievements by category
export const getAchievementsByCategory = () => {
  const categories = {};
  
  Object.values(ACHIEVEMENTS).forEach(achievement => {
    if (!categories[achievement.category]) {
      categories[achievement.category] = [];
    }
    categories[achievement.category].push(achievement);
  });
  
  return categories;
};

// Utility function to manually award company founder badge to a team
export const awardCompanyFounderBadgeToTeam = async (teamId, db) => {
  try {
    const { doc, getDoc, updateDoc } = await import('firebase/firestore');
    
    // Get current team data
    const teamDoc = await getDoc(doc(db, 'teams', teamId));
    if (!teamDoc.exists()) {
      throw new Error('Team not found');
    }
    
    const teamData = teamDoc.data();
    const currentAchievements = teamData.achievements || [];
    
    // Check if already has the badge
    if (currentAchievements.includes('company_founder')) {
      console.log('Team already has company founder badge');
      return false;
    }
    
    // Add the badge
    const updatedAchievements = [...currentAchievements, 'company_founder'];
    await updateDoc(doc(db, 'teams', teamId), {
      achievements: updatedAchievements
    });
    
    console.log('Company founder badge awarded to team successfully');
    return true;
  } catch (error) {
    console.error('Error awarding company founder badge to team:', error);
    return false;
  }
};

// Utility function to manually award company founder badge to a team by captain email
export const awardCompanyFounderBadgeToTeamByEmail = async (captainEmail, db) => {
  try {
    const { collection, query, where, getDocs, updateDoc, doc, getDoc } = await import('firebase/firestore');
    
    // Find user by email
    const usersQuery = query(collection(db, 'users'), where('email', '==', captainEmail));
    const usersSnapshot = await getDocs(usersQuery);
    
    if (usersSnapshot.empty) {
      throw new Error('User not found with that email');
    }
    
    const userDoc = usersSnapshot.docs[0];
    const userData = userDoc.data();
    
    if (!userData.teamId) {
      throw new Error('User is not part of a team');
    }
    
    // Get team data
    const teamDoc = await getDoc(doc(db, 'teams', userData.teamId));
    if (!teamDoc.exists()) {
      throw new Error('Team not found');
    }
    
    const teamData = teamDoc.data();
    const currentAchievements = teamData.achievements || [];
    
    // Check if already has the badge
    if (currentAchievements.includes('company_founder')) {
      console.log('Team already has company founder badge');
      return false;
    }
    
    // Add the badge
    const updatedAchievements = [...currentAchievements, 'company_founder'];
    await updateDoc(doc(db, 'teams', userData.teamId), {
      achievements: updatedAchievements
    });
    
    console.log('Company founder badge awarded to team successfully for captain', captainEmail);
    return true;
  } catch (error) {
    console.error('Error awarding company founder badge to team:', error);
    return false;
  }
}; 